<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">Understanding Randomness</h2>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">How does the wheel's randomness work?</h3>
      <p>Spin the Names uses a true random selection mechanism. Unlike some similar tools that might pre-determine the
        result before animation, our wheel's outcome is genuinely determined by where the spinner stops after a
        physics-based animation.</p>
      <p class="mt-2">When you click the spin button or press the spacebar:</p>
      <ol class="list-decimal ml-6 mt-2">
        <li>The wheel is given a random initial velocity</li>
        <li>Physics-based deceleration is applied over time</li>
        <li>The final resting position determines the winner</li>
      </ol>
      <p class="mt-2">This approach ensures that the selection process is fair and truly random.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Is the wheel truly random?</h3>
      <p>Yes, the wheel uses JavaScript's cryptographically strong random number generation to determine the initial
        spin parameters. The outcome isn't predetermined - it's calculated in real-time based on the physics of the spin
        and where the wheel naturally comes to rest.</p>
      <p class="mt-2">To ensure fairness:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>Each entry has an equal chance of being selected if all slices are of equal size</li>
        <li>The animation is purely visual and doesn't manipulate the outcome</li>
        <li>The same random number generators used in cryptography help determine the spin dynamics</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Can I influence which option gets picked?</h3>
      <p>The wheel's selection cannot be influenced once spinning has begun. The only ways to affect probability are:
      </p>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>Adding duplicate entries:</strong> If you add the same name multiple times, it will have a
          proportionally higher chance of being selected (e.g., entering "Option A" three times gives it three times the
          probability)</li>
        <li><strong>Adjusting slice sizes:</strong> Some advanced options allow for different sized slices, which
          affects probability proportionally</li>
      </ul>
      <p class="mt-2">Beyond these legitimate methods, there is no way to manipulate or predict the outcome of a spin.
      </p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Technical implementation details</h3>
      <p>For those interested in the technical details:</p>
      <ul class="list-disc ml-6 mt-2">
        <li>The wheel uses the browser's <code>crypto.getRandomValues()</code> when available, falling back to
          Math.random() with additional entropy in older browsers</li>
        <li>The physics model includes angular momentum, friction, and deceleration algorithms</li>
        <li>The random values determine:</li>
        <ul class="list-disc ml-6 mt-2">
          <li>Initial angular velocity (how fast the wheel spins)</li>
          <li>Deceleration rate (how quickly it slows down)</li>
          <li>Small variance factors (for natural-feeling motion)</li>
        </ul>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Why does it sometimes land near the same spot?</h3>
      <p>While the wheel is truly random, human perception tends to notice patterns even in random data. If you spin the
        wheel many times, the results should distribute evenly across all options over the long term. Any perceived
        patterns are simply statistical variance, which is normal in random systems.</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
